import { VIDEO_TYPE } from "./../ricos-content/index.js";
import {
  createOpenMediaManagerFn,
  MediaManagerMediaType
} from "./../_shared/create-open-media-manager-fn.js";
import { getHighestQualityVideo } from "./../_shared/get-highest-quality-video.js";
import { getUploadError } from "./../_shared/get-upload-error.js";
import { logInvariant } from "./../_shared/log-invariant.js";
import { MediaType } from "./../_shared/request-service-provider/consts.js";
import { createUploadFn } from "./../_shared/request-service-provider/media-manager-uploader.js";
import { unreachable } from "./../_shared/uncreachable.js";
import { upload } from "./../_shared/upload/upload.js";
import { withFileSizeLimitation } from "./../_shared/with-file-size-limitation.js";
import { RICOS_DEFAULTS } from "./constants.js";
const isPickMediaType = (data) => "type" in data && data.type === "video";
const getVideoAttributes = ({
  uploadData,
  componentData,
  config
}) => {
  const disableDownload = componentData?.video?.src ? componentData.disableDownload : config.disableDownloadDefault || null;
  if (isPickMediaType(uploadData)) {
    return {
      video: { src: uploadData.src },
      ...uploadData.thumbnail ? {
        thumbnail: uploadData.thumbnail
      } : {},
      ...uploadData.title ? { title: uploadData.title } : {},
      disableDownload
    };
  }
  const { pathname, thumbnail: uploadedThumbnail, url: uploadedUrl, title } = uploadData;
  const src = pathname ? { id: pathname } : { url: uploadedUrl };
  const thumbnailObject = uploadedThumbnail ? {
    thumbnail: {
      src: { id: uploadedThumbnail.pathname },
      width: uploadedThumbnail.width,
      height: uploadedThumbnail.height
    }
  } : {};
  const titleObject = title ? { title } : {};
  return {
    video: { src },
    ...thumbnailObject,
    ...titleObject,
    disableDownload
  };
};
const getVideoPickMedia = ({
  services,
  handleFileUpload
}) => {
  const editorPickMedia = services.editorProps.pickMedia?.pickVideo;
  const devPickMedia = services.devService().pickMedia;
  const handleFileSelection = services.devService().handleFileSelection ?? createHandleFileSelectionFn(services);
  return devPickMedia ? devPickMedia : editorPickMedia ? editorPickMedia : handleFileSelection ? async () => {
    const data = await new Promise(
      (resolve) => handleFileSelection(({ data: data2 }) => resolve(data2))
    );
    return { type: "ready-media", media: [data] };
  } : async ({ mimeType, multiple }) => {
    const files = await services.uploadService?.selectFiles({ accept: mimeType, multiple }) ?? [];
    return { type: "file", file: files, customUploadFn: handleFileUpload };
  };
};
const handleVideoUpload = async ({
  services,
  config,
  publicCommands,
  nodeId
}) => {
  const handleFileUpload = createHandleFileUploadFn(services);
  const pickMedia = getVideoPickMedia({ services, handleFileUpload });
  const result = await pickMedia({ mimeType: "video/*", multiple: false });
  const clearUploadToken = (targetNodeId) => {
    publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
      ...publicCommands?.getBlockComponentData(targetNodeId),
      uploadToken: null
    });
  };
  const getUploadToken = (targetNodeId) => {
    return publicCommands?.getBlockComponentData(targetNodeId)?.uploadToken;
  };
  switch (result.type) {
    case "ready-media": {
      const data = result.media[0] || {};
      if (nodeId) {
        const componentData = publicCommands?.getBlockComponentData(nodeId);
        publicCommands?.setBlock(nodeId, VIDEO_TYPE, {
          ...componentData,
          ...getVideoAttributes({ uploadData: data, componentData, config })
        });
        return;
      }
      queueMicrotask(() => {
        publicCommands.insertBlockWithBlankLines(
          VIDEO_TYPE,
          {
            ...getVideoAttributes({
              uploadData: data,
              componentData: null,
              config
            }),
            ...RICOS_DEFAULTS
          },
          {
            updateSelection: true
          }
        );
      });
      return;
    }
    case "file": {
      const files = result.file;
      const targetNodeId = nodeId || publicCommands.insertBlockWithBlankLines(
        VIDEO_TYPE,
        {
          ...RICOS_DEFAULTS,
          loading: true
        },
        {
          updateSelection: true
        }
      );
      const uploadToken = Symbol("upload-token");
      const file = Array.isArray(files) ? files[0] : files;
      const trackBi = services.uploadService.trackUploadBi(VIDEO_TYPE, file.size, file.name);
      try {
        const url = URL.createObjectURL(file);
        await Promise.resolve();
        publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
          ...publicCommands?.getBlockComponentData(targetNodeId),
          video: {
            src: { url }
          },
          disableDownload: config.disableDownloadDefault,
          loading: true,
          error: null,
          uploadToken
        });
        trackBi?.start();
        const uploadFn = result.customUploadFn || handleFileUpload;
        if (!uploadFn) {
          logInvariant(Error("No upload function provided"));
          throw new Error("No upload function provided");
        }
        const uploadedFile = await upload(uploadFn, file);
        if (getUploadToken(targetNodeId) !== uploadToken) {
          URL.revokeObjectURL(url);
          return;
        }
        const uploadData = uploadedFile.data || {};
        const componentData = publicCommands?.getBlockComponentData(targetNodeId);
        publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
          ...componentData,
          ...getVideoAttributes({ uploadData, componentData, config }),
          tempData: null,
          loading: null,
          error: null
        });
        URL.revokeObjectURL(url);
        trackBi?.success();
      } catch (error) {
        if (getUploadToken(targetNodeId) === uploadToken) {
          const componentData = publicCommands?.getBlockComponentData(targetNodeId);
          publicCommands?.setBlock(targetNodeId, VIDEO_TYPE, {
            ...componentData,
            error,
            loading: null
          });
          trackBi?.error(error);
          services.uploadService.notifyError(error);
        }
      } finally {
        if (getUploadToken(targetNodeId) === uploadToken) {
          clearUploadToken(targetNodeId);
        }
      }
      return;
    }
    case "cancel": {
      return;
    }
    default: {
      console.error(unreachable(result));
      return;
    }
  }
};
const createHandleFileUploadFn = (services) => {
  const { metaSiteId, uid, siteOwnerId } = services.wixInstance();
  if (!metaSiteId) {
    return void 0;
  }
  const videoUpload = (file, updateEntity) => {
    const upload2 = createUploadFn(services);
    return upload2(file, MediaType.VIDEO).then(({ file_output: { video, image } }) => {
      updateEntity({
        data: {
          pathname: getHighestQualityVideo(video).url,
          thumbnail: {
            pathname: image[0].url,
            width: image[0].width,
            height: image[0].height
          },
          title: file.name
        }
      });
    }).catch((error) => {
      const isOwner = siteOwnerId === uid;
      const mediaUploadError = getUploadError(error, metaSiteId, isOwner);
      updateEntity({ error: mediaUploadError });
    });
  };
  return withFileSizeLimitation(videoUpload, services.editorProps.fileUploadSizeLimit);
};
const createHandleFileSelectionFn = (services) => {
  const openMediaManager = createOpenMediaManagerFn();
  if (!openMediaManager) {
    return void 0;
  }
  return (updateEntity) => openMediaManager({
    mediaType: MediaManagerMediaType.VIDEO,
    appDefId: services.wixInstance().appDefId
  }).then((items) => {
    const { fullTitle, title, fileOutput } = items[0];
    const { video, image } = fileOutput;
    const { url: pathname } = getHighestQualityVideo(video);
    const { url, width, height } = image[0];
    const thumbnail = { pathname: url, width, height };
    const videoTitle = fullTitle ?? title;
    const titleObject = videoTitle ? { title: videoTitle } : {};
    updateEntity({ data: { pathname, thumbnail, ...titleObject } });
  });
};
export {
  handleVideoUpload
};
//# sourceMappingURL=handle-video-upload.js.map
